import { Fragment, StrictMode } from 'react'
import type { VFC } from 'react'
import { Size } from '@opallabs/gem'
import { Conditional } from '@opallabs/kuiper'
import {
  Button,
  ButtonIconPosition,
  Icon,
  screenreaderOnly,
} from '@opallabs/lapidary'
import * as styles from './styles'
import type { HeaderProps } from './types'

export const Header: VFC<HeaderProps> = ({
  canEdit = false,
  currentIndex = 0,
  fullscreen = false,
  onEdit,
  onNavigateIndex,
  onNavigateNext,
  onNavigatePrevious,
  onShare,
  onToggleFullscreen,
  slideTitle,
  theme = 'light',
  totalCount = 0,
}) => {
  const Control = ({
    buttonText,
    disabled = false,
    glyph,
    onClick,
    name,
    useHypernova = false,
  }: {
    buttonText: string
    disabled?: boolean
    glyph: string
    onClick: () => void
    name: string
    useHypernova?: boolean
  }) => (
    <li css={styles.control}>
      <Button
        buttonText={buttonText}
        variant="ghost"
        disabled={disabled}
        hideButtonText={true}
        icon={
          <Icon
            ariaHidden={true}
            glyph={glyph}
            height={Size.Icon}
            useHypernova={useHypernova}
            width={Size.Icon}
          />
        }
        iconPosition={ButtonIconPosition.leading}
        name={name}
        onClick={onClick}
        styleOverrides={{
          button: styles.controlButton,
        }}
        theme={theme}
      />
    </li>
  )

  return (
    <StrictMode>
      <div css={styles.container}>
        <div aria-live="assertive" css={styles.positionInDeck({ theme })}>
          <span aria-hidden={true}>{currentIndex + 1}</span>
          <span css={screenreaderOnly}>
            Slide {currentIndex + 1} of {totalCount}: {slideTitle}
          </span>
        </div>

        <ul css={styles.controls({ theme })}>
          <Conditional condition={canEdit}>
            <Fragment>
              <Control
                buttonText="Share this presentation"
                glyph="gem-share"
                name="share"
                onClick={onShare}
              />
              <Control
                buttonText="Edit this presentation"
                glyph="gem-edit"
                name="edit"
                onClick={onEdit}
              />
            </Fragment>
          </Conditional>

          <Control
            buttonText={
              fullscreen ? 'Exit full-screen mode' : 'Enter full-screen mode'
            }
            glyph={fullscreen ? 'collapse' : 'expand'}
            name="fullscreen"
            onClick={onToggleFullscreen}
            useHypernova={true}
          />

          <Control
            buttonText="View all slides in this presentation"
            glyph="gem-grid"
            name="grid"
            onClick={onNavigateIndex}
          />
          <Control
            buttonText="Navigate to previous slide"
            disabled={currentIndex <= 0}
            glyph="gem-chevron-w"
            name="previous"
            onClick={onNavigatePrevious}
          />
          <Control
            buttonText="Navigate to next slide"
            disabled={currentIndex >= totalCount - 1}
            glyph="gem-chevron-e"
            name="next"
            onClick={onNavigateNext}
          />
        </ul>
      </div>
    </StrictMode>
  )
}
Header.displayName = 'Hubble: Header ← SlideViewer'
